import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {unpinIssueMutation} from './__generated__/unpinIssueMutation.graphql'

export function commitUnpinIssueMutation({
  environment,
  input,
  repositoryId,
  currentPinnedCount,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {issueId: string}
  repositoryId?: string
  currentPinnedCount?: number
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const optimisticResponse =
    repositoryId !== undefined && currentPinnedCount !== undefined
      ? {
          unpinIssue: {
            id: null,
            issue: {
              id: input.issueId,
              isPinned: false,
              repository: {
                id: repositoryId,
                pinnedIssues: {
                  totalCount: Math.max(0, currentPinnedCount - 1),
                },
              },
            },
          },
        }
      : undefined

  // Only the issue's own field: the repository's `pinnedIssues.totalCount` belongs to a different
  // entity, so it would be a second operation targeting `repositoryId` rather than a nested write
  // through the issue. No consumer needs that count yet.
  const optimistic = publishOptimisticFields(input.issueId, {isPinned: false})

  let disposable
  try {
    disposable = commitMutation<unpinIssueMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation unpinIssueMutation($input: UnpinIssueInput!) @raw_response_type {
          unpinIssue(input: $input) {
            id @deleteRecord
            issue {
              isPinned
              repository {
                pinnedIssues(first: 3) {
                  totalCount
                }
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticResponse,
      onCompleted: (response, errors) => {
        if (errors?.length) {
          const error = new Error(errors[0]?.message ?? 'Failed to unpin issue')
          optimistic.failed(error)
          onError?.(error)
          return
        }
        optimistic.completed({isPinned: response.unpinIssue?.issue?.isPinned ?? false})
        onCompleted?.()
      },
      onUnsubscribe: () => optimistic.failed(new Error('Unpinning the issue was cancelled')),
      onError: error => {
        optimistic.failed(error)
        onError?.(error)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to unpin issue'))
    throw error
  }

  return disposable
}
